/**
 * pwaService.js — PWA Service Worker registration, Install Prompt, and Push Subscriptions
 *
 * Handles:
 *   - Service worker registration (offline caching, background sync)
 *   - Silent update (A9): SKIP_WAITING + optional reload when safe (no unsaved forms)
 *   - "Install App" prompt (add to home screen)
 *   - Browser push subscription lifecycle
 *
 * Usage:
 *   import pwaService from '@/services/pwaService';
 *   pwaService.register();                 // call once on app boot
 *   pwaService.showInstallPrompt();
 *   pwaService.isInstalled();
 *   pwaService.subscribeToPush(vapidPublicKey);
 */

const SW_PATH = '/service-worker.js';

let deferredUpdatePending = false;
let controllerReloadListenerAttached = false;

function hasUnsavedChanges() {
  return typeof window !== 'undefined' && window.__hfrcHasUnsavedChanges === true;
}

/**
 * When a new worker is waiting, call skipWaiting. If a previous controller existed,
 * reload after controllerchange so chunk URLs match the new build.
 */
function tryApplyWaitingWorker(registration) {
  if (!registration?.waiting) return;

  if (hasUnsavedChanges()) {
    deferredUpdatePending = true;
    window.dispatchEvent(new CustomEvent('swUpdateDeferred'));
    return;
  }

  const hadController = !!navigator.serviceWorker.controller;

  window.dispatchEvent(
    new CustomEvent('swUpdateApplying', {
      detail: { hadController },
    })
  );

  if (hadController && !controllerReloadListenerAttached) {
    controllerReloadListenerAttached = true;
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.location.reload();
      },
      { once: true }
    );
  }

  try {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  } catch (err) {
    console.warn('[PWA] Failed to post SKIP_WAITING:', err);
  }
}

function attachInstallingListeners(registration) {
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state !== 'installed') return;
      // New worker installed — activate via message (never immediate skipWaiting in SW install).
      if (registration.waiting) {
        tryApplyWaitingWorker(registration);
      }
    });
  });
}

// ── Registration ─────────────────────────────────────────────────────────────

async function register() {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
    });
    console.log('[PWA] Service worker registered:', registration.scope);

    attachInstallingListeners(registration);

    const kickWaitingIfAny = () => {
      if (registration.waiting) {
        tryApplyWaitingWorker(registration);
      }
    };

    kickWaitingIfAny();
    const installing = registration.installing;
    if (installing) {
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed') {
          kickWaitingIfAny();
        }
      });
    }

    await navigator.serviceWorker.ready;

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        window.dispatchEvent(
          new CustomEvent('pushSubscriptionChanged', {
            detail: event.data.subscription,
          })
        );
      }
    });

    return registration;
  } catch (err) {
    console.error('[PWA] Service worker registration failed:', err);
    return null;
  }
}

/**
 * Called on SPA navigation — retries SKIP_WAITING if update was deferred due to unsaved state.
 */
function retryDeferredUpdateAfterNavigation() {
  if (!deferredUpdatePending) return;
  if (hasUnsavedChanges()) return;

  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg?.waiting) {
      deferredUpdatePending = false;
      tryApplyWaitingWorker(reg);
    } else {
      deferredUpdatePending = false;
    }
  });
}

// ── Install Prompt ───────────────────────────────────────────────────────────
let deferredInstallPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
  window.dispatchEvent(new CustomEvent('pwaInstallAvailable'));
});

async function showInstallPrompt() {
  if (!deferredInstallPrompt) return false;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === 'accepted';
}

function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// ── Push subscriptions ────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

async function subscribeToPush(vapidPublicKey) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Push notifications not supported in this browser');
  }
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  const appServerKey = urlBase64ToUint8Array(vapidPublicKey);
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: appServerKey,
  });
}

async function unsubscribeFromPush() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
}

async function isPushSubscribed() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

const pwaService = {
  register,
  retryDeferredUpdateAfterNavigation,
  showInstallPrompt,
  isInstalled,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
};
export default pwaService;
